<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import Slider from '../components/Slider.vue';
import CustomSelect from '../../shared/components/CustomSelect.vue';
import { getAssets, calculatePriceForLanding } from '../../../../repositories/landing.repository';
import { getTypeMoney } from '../../../../helpers/getTypeMoneyByCountry';
import {
  calculatePolicyPriceFromTax,
  type PolicyTax,
} from '../../../../helpers/calculatePolicyPriceFromTax';
import { useToast } from '../../../../composables/useToast';
import { useI18n } from 'vue-i18n';
import storePolicy from '@store/policy';

const emit = defineEmits<{ (e: 'next'): void }>();

const SUBDOMAIN = 'MEXICO';
const { t } = useI18n();
const store = storePolicy();
const route = useRoute();
const { $toast } = useToast();

const transport = ref<string>('terrestre');
const assetOptions = ref<any[]>([]);
const selectedAssetId = ref<any>(null);
const indemnificationLimit = ref<number>(200000);
const endPrice = ref<number>(0);
const errorResponse = ref<string | null>(null);
const loadingTax = ref<boolean>(false);
const flash = ref<boolean>(false);
const landingTax = ref<PolicyTax | null>(null);

const transportOptions = computed(() => [
  { label: t('landing.wizard.cotiza.opt_terrestre'), value: 'terrestre' },
  { label: t('landing.wizard.cotiza.opt_maritimo'), value: 'maritimo', disabled: true },
  { label: t('landing.wizard.cotiza.opt_paqueteria'), value: 'paqueteria', disabled: true },
]);

const selectedAsset = computed(() =>
  assetOptions.value.find((a: any) => a.id === selectedAssetId.value),
);
const canQuote = computed(
  () => transport.value === 'terrestre' && !!selectedAsset.value,
);
const sliderDisabled = computed(() => !selectedAsset.value || loadingTax.value);

const applyLocalPrice = (withFlash = false) => {
  if (!landingTax.value) {
    endPrice.value = 0;
    return;
  }
  try {
    endPrice.value = calculatePolicyPriceFromTax(
      landingTax.value,
      indemnificationLimit.value,
      true,
    );
    errorResponse.value = null;
    if (withFlash) {
      flash.value = true;
      setTimeout(() => (flash.value = false), 200);
    }
  } catch (error: any) {
    endPrice.value = 0;
    errorResponse.value = error.message;
  }
};

const fetchLandingTax = async () => {
  if (!selectedAsset.value) return;
  const response = await calculatePriceForLanding({
    riskType: selectedAsset.value.riskType ?? { description: 'PAQUETERIA' },
    indemnificationLimitMax: indemnificationLimit.value,
    assetId: selectedAsset.value.id,
  });
  landingTax.value = response.tax;
  applyLocalPrice(true);
};

onMounted(async () => {
  try {
    assetOptions.value = (await getAssets()) as unknown as any[];
  } catch (error) {
    $toast?.error('Error fetching assets');
  }
});

const handleSliderChange = (value: number) => {
  indemnificationLimit.value = value;
  if (!landingTax.value) return;

  const min = landingTax.value.indemnificationLimitMin;
  const max = landingTax.value.indemnificationLimitMax;
  const outOfRange =
    (min && min > value) || (max && max < value);

  if (outOfRange) {
    fetchLandingTax().catch((error: any) => {
      errorResponse.value = error.message;
    });
    return;
  }
  applyLocalPrice();
};

const handleAssetChange = async () => {
  endPrice.value = 0;
  errorResponse.value = null;
  landingTax.value = null;
  if (!selectedAsset.value) return;

  loadingTax.value = true;
  try {
    await fetchLandingTax();
  } catch (error: any) {
    errorResponse.value = error.message;
  } finally {
    loadingTax.value = false;
  }
};

const handleContinue = () => {
  if (endPrice.value === 0 || !selectedAsset.value) return;

  store.setProp({
    key: 'data',
    value: {
      asset: selectedAsset.value,
      subAsset: null,
      product: null,
      indemnificationLimit: Number(indemnificationLimit.value.toFixed(0)),
      endPrice: endPrice.value,
      taxId: landingTax.value?.id,
      queryParams: route.query,
    },
  });

  emit('next');
};

const money = computed(() => getTypeMoney(SUBDOMAIN));
</script>

<template>
  <section class="wz-screen">
    <div class="wz-grid wz-grid--hero">
      <div class="wz-hero-text">
        <h1>
          {{ t('landing.wizard.cotiza.titulo_1') }}<br /><span class="accent">{{
            t('landing.wizard.cotiza.titulo_2')
          }}</span>
        </h1>
        <p class="wz-lead">
          <strong>{{ t('landing.wizard.cotiza.lead_bold') }}</strong>
          {{ t('landing.wizard.cotiza.lead') }}
        </p>
        <ul class="wz-benefits">
          <li class="wz-benefit">
            <span class="wz-benefit-check">✓</span>
            <span
              ><strong>{{ t('landing.wizard.cotiza.b1_bold') }}</strong>
              {{ t('landing.wizard.cotiza.b1') }}</span
            >
          </li>
          <li class="wz-benefit">
            <span class="wz-benefit-check">✓</span>
            <span
              ><strong>{{ t('landing.wizard.cotiza.b2_bold') }}</strong>
              {{ t('landing.wizard.cotiza.b2') }}</span
            >
          </li>
          <li class="wz-benefit">
            <span class="wz-benefit-check">✓</span>
            <span
              ><strong>{{ t('landing.wizard.cotiza.b3_bold') }}</strong>
              {{ t('landing.wizard.cotiza.b3') }}</span
            >
          </li>
          <li class="wz-benefit">
            <span class="wz-benefit-check">✓</span>
            <span
              ><strong>{{ t('landing.wizard.cotiza.b4_bold') }}</strong>
              {{ t('landing.wizard.cotiza.b4') }}</span
            >
          </li>
        </ul>
      </div>

      <aside class="wz-card">
        <span class="wz-card-tag">{{ t('landing.wizard.cotiza.tag') }}</span>
        <h2>{{ t('landing.wizard.cotiza.titulo_card') }}</h2>
        <p class="sub">{{ t('landing.wizard.cotiza.subtitulo_card') }}</p>

        <div class="wz-field">
          <label>{{ t('landing.wizard.cotiza.tipo_transporte') }}</label>
          <CustomSelect v-model="transport" :options="transportOptions" />
          <div class="wz-help">
            {{ t('landing.wizard.cotiza.transporte_help') }}
          </div>
        </div>

        <div class="wz-field">
          <label>{{ t('landing.wizard.cotiza.tipo_mercancia') }}</label>
          <CustomSelect
            v-model="selectedAssetId"
            :options="assetOptions"
            label-key="nombre"
            value-key="id"
            :placeholder="t('landing.wizard.cotiza.elige_opcion')"
            @change="handleAssetChange"
          />
        </div>

        <div class="wz-slider-block" :class="{ 'is-disabled': sliderDisabled }">
          <div class="top">
            <span>{{ t('landing.wizard.cotiza.suma_asegurada') }}</span>
            <span class="wz-slider-value">
              {{ money }}
              {{
                indemnificationLimit
                  .toFixed(0)
                  .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
              }}
            </span>
          </div>
          <Slider
            :subDomainInitial="SUBDOMAIN"
            :disabled="sliderDisabled"
            @value="handleSliderChange"
          />
          <div class="wz-slider-hint">
            <template v-if="sliderDisabled">
              {{ t('landing.wizard.cotiza.slider_disabled') }}
            </template>
            <template v-else>
              {{ t('landing.wizard.cotiza.slider_hint_pre') }} <kbd>←</kbd>
              <kbd>→</kbd> {{ t('landing.wizard.cotiza.slider_hint_post') }}
            </template>
          </div>
        </div>

        <div class="wz-live-price" :class="{ flash }">
          <div class="wz-live-price-label">
            {{ t('landing.wizard.cotiza.precio_estimado') }}
          </div>
          <div class="wz-live-price-amount">
            {{ endPrice !== 0 ? `${money} ${endPrice}` : `${money} —` }}
          </div>
          <div class="wz-live-price-iva">
            {{ t('landing.wizard.cotiza.precio_nota') }}
          </div>
        </div>

        <p v-if="errorResponse" class="wz-error">{{ errorResponse }}</p>

        <button
          type="button"
          class="wz-cta"
          :disabled="!canQuote || endPrice === 0 || loadingTax"
          @click="handleContinue"
        >
          {{
            loadingTax
              ? t('landing.wizard.cotiza.calculando')
              : t('landing.wizard.cotiza.continuar')
          }}
        </button>

        <p class="wz-privacy">{{ t('landing.wizard.cotiza.privacidad') }}</p>
      </aside>
    </div>
  </section>

  <section class="wz-trust">
    <div class="wz-trust-inner">
      <div class="wz-trust-stat">
        <div class="num">{{ t('landing.wizard.trust.stat_num') }}</div>
        <div class="label">{{ t('landing.wizard.trust.stat_label') }}</div>
      </div>
      <div>
        <div class="wz-partners-label">
          {{ t('landing.wizard.trust.partners_label') }}
        </div>
        <div class="wz-partners">
          <span class="wz-partner-chip assistcargo">AssistCargo</span>
          <span class="wz-partner-chip">OnTracking</span>
          <span class="wz-partner-chip">Atlántida</span>
          <span class="wz-partner-chip">Encontrack</span>
          <span class="wz-partner-chip">4GPS</span>
          <span class="wz-partner-chip">Argos</span>
          <span class="wz-partner-chip">{{ t('landing.wizard.trust.partners_mas') }}</span>
        </div>
      </div>
    </div>
  </section>

  <section class="wz-how">
    <div class="wz-how-inner">
      <h2>
        {{ t('landing.wizard.how.titulo_1') }}
        <span class="accent">{{ t('landing.wizard.how.titulo_2') }}</span>
      </h2>
      <p class="wz-how-sub">{{ t('landing.wizard.how.sub') }}</p>
      <div class="wz-how-grid">
        <div class="wz-how-step">
          <span class="num">01</span>
          <h3>{{ t('landing.wizard.how.s1_titulo') }}</h3>
          <p>{{ t('landing.wizard.how.s1_desc') }}</p>
        </div>
        <div class="wz-how-step">
          <span class="num">02</span>
          <h3>{{ t('landing.wizard.how.s2_titulo') }}</h3>
          <p>{{ t('landing.wizard.how.s2_desc') }}</p>
        </div>
        <div class="wz-how-step">
          <span class="num">03</span>
          <h3>{{ t('landing.wizard.how.s3_titulo') }}</h3>
          <p>{{ t('landing.wizard.how.s3_desc') }}</p>
        </div>
        <div class="wz-how-step">
          <span class="num">04</span>
          <h3>{{ t('landing.wizard.how.s4_titulo') }}</h3>
          <p>{{ t('landing.wizard.how.s4_desc') }}</p>
        </div>
        <div class="wz-how-step">
          <span class="num">05</span>
          <h3>{{ t('landing.wizard.how.s5_titulo') }}</h3>
          <p>{{ t('landing.wizard.how.s5_desc') }}</p>
        </div>
      </div>
    </div>
  </section>
</template>
